/* ==========================================================================
   dtc-special.js, the Special Collection cinematic reveal.

   NO SCROLL HIJACKING. There is no wheel handler, no scrollTo, no scroll
   locking, no overflow toggling on <body>. The "hold" is a plain CSS
   `position: sticky` stage; this file only reads the section's rect and
   writes transforms.

   MOTION (exact, from the design prototype)
   -----------------------------------------
     step     = 360 / cardCount            (72 deg with the design's 5 shirts)
     trigger  : first frame where r.top < vh*.55 && r.bottom > vh*.45
                -> played = true, showStart = now
     t        = showStart ? (now - showStart) / 1000 : 99
     showing  = showStart && t < 4.2

     target   = showing ? 1.6 - 1.25 * cl((t - 1.4) / 1.2) : 0.16
     vel     += (target - vel) * 0.07
     rot     += vel                        // whip, then decelerate to a drift

     chip hover (only when NOT showing):
                vel = 0
                snapT ??= round(rot / step) * step
                rot += (snapT - rot) * 0.16
     chip leave: snapT = null
     chip click i:
                d = norm(-i * step - rot); if (d > 180) d -= 360
                snapT = rot + d            // latched until it lands, so a
                                           // touch tap (which fires
                                           // pointerleave immediately) still
                                           // completes its rotation

     per card i:
                a  = (rot + i * step) * PI / 180
                R2 = min(stageWidth * radiusFactor, radiusMax)
                RV = min(R2 * 0.42, vh * 0.155)
                x  = sin(a) * R2
                y  = cos(a) * RV - 26
                rz = sin(a) * 7
                sc = (0.42 + 0.58 * (cos(a) + 1) / 2) * (0.7 + 0.3 * intro)
                op = (0.3 + 0.7 * (cos(a) + 1) / 2) * intro
                z  = 100 + round(cos(a) * 100)
                front = the index with the largest cos

     intro    = showStart ? cl(t / 0.9) : 0
                title opacity = intro, translateY = (1 - intro) * 46px
     c2       = showStart ? cl((t - 3.1) / 0.7) : 0
                CTA opacity = c2, translate(-50%, (1 - c2) * 26px),
                pointer-events auto above .5; chip row the same
     progress = showing ? cl(t / 4.2) * 100 + '%' : '0'

   PERFORMANCE
   -----------
   One rAF loop, cancelled entirely while the section is off screen
   (IntersectionObserver) and while the tab is hidden. One rect read per
   frame; the stage width and the CSS-driven radius factors are cached and
   only re-measured on resize.

   NOT ENHANCED AT ALL under prefers-reduced-motion, the server-rendered fan
   stays exactly as it is, fully visible and shoppable.

   No libraries. Every rAF, listener and observer is released on cleanup so
   the theme editor can mount and unmount this section freely.
   ========================================================================== */
(function () {
  'use strict';

  var DTC = window.DTC;
  if (!DTC || !DTC.register) return;

  /* Deferred scripts run after parsing but before first paint, so flipping
     this class here is what lets the CSS pin the stage without a flash of
     un-pinned layout. Without JS the class never lands and the section stays
     in its static, fully shoppable form. */
  document.documentElement.classList.add('dtc-sp-js');

  function cl(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }
  function norm(x) { return ((x % 360) + 360) % 360; }

  /* Degrees per frame once the intro whip has decayed. The design prototype
     idled at 0.16 (~9.6 deg/s at 60fps); this is the calmer drift. */
  var DRIFT = 0.085;
  /* Opening whip: peak speed, and how much of it bleeds off during the intro.
     It lands exactly on DRIFT so there is no step when the intro hands over. */
  var WHIP = 1.1;
  var WHIP_DECAY = WHIP - DRIFT;

  /* --- The interaction model ------------------------------------------- *
   *
   * THE CAROUSEL is ambient. It drifts, forever, and the shirt at the front
   * is simply the biggest and brightest one. Nothing about it demands input.
   *
   * THE BUBBLES are the index. Clicking one eases that shirt round to the
   * front. They are the only control that MOVES the wheel deliberately.
   *
   * HOVER is inspection. It does two things and no more: the drift coasts to
   * a stop so the shirt you are looking at stops walking away from you, and
   * the shirt under the cursor lifts slightly out of the fan. It does NOT
   * teleport the card to the middle, and it does not blow it up to fill the
   * stage, that used to happen, and it read as a jump-cut every time,
   * because a moving object was yanked across the screen and doubled in size
   * while you were still looking at it. Clicking opens the product page,
   * which is where a shirt is actually meant to be seen big.
   *
   * LIFT_SCALE stays small on purpose: enough that the shirt visibly comes
   * forward, not so much that the composition rearranges itself. */
  var LIFT_SCALE = 0.10;   /* +10% at full lift */
  var LIFT_RISE = 14;      /* px it floats upward */
  var LIFT_EASE = 0.14;    /* per-frame approach, ~250ms to settle */

  /* How fast the wheel gives up its speed when the pointer arrives, and picks
     it back up when the pointer leaves. Low enough to read as "easing to a
     rest", high enough that it does not feel unresponsive. */
  var SETTLE = 0.055;

  /* DRAG. The wheel follows the finger 1:1 at the front of the orbit: one
     screen pixel of drag moves the front shirt one screen pixel. That is not
     a taste constant, it is geometry, x = sin(a) * R2, so dx/da = R2 at the
     front and the conversion is 180/PI/R2 degrees per pixel. Tying it to the
     measured radius means the shirt tracks the finger at every viewport
     instead of feeling heavy on a phone and twitchy on a monitor.
     MAX_FLING caps a violent flick so the drop stays readable rather than
     becoming a blur. SLOP is the travel that separates a tap from a drag. */
  var DEG_PER_RAD = 180 / Math.PI;
  var MAX_FLING = 3;    /* deg/frame ~ 180 deg/s */
  var SLOP = 8;         /* px before a press counts as a drag */

  var canHover = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  DTC.register('special', {
    init: function (root) {
      var stage = root.querySelector('[data-sp-stage]');
      var cards = Array.prototype.slice.call(root.querySelectorAll('[data-sp-card]'));
      if (!stage || cards.length < 2) return;

      /* Reduced motion → leave the server-rendered fan completely alone. */
      if (DTC.reducedMotion) return;

      var title = root.querySelector('[data-sp-title]');
      var cta = root.querySelector('[data-sp-cta]');
      var chipRow = root.querySelector('[data-sp-chips]');
      var chips = Array.prototype.slice.call(root.querySelectorAll('[data-sp-chip]'));
      var prog = root.querySelector('[data-sp-prog]');

      var step = 360 / cards.length;

      /* State (the prototype's spRot / spVel / spSnapT / spPlayed /
         spShowStart / spChipHover). */
      var spRot = 0;
      var spVel = DRIFT;
      var spSnapT = null;
      var spSnapHold = false;
      var spPlayed = false;
      var spShowStart = 0;
      var spChipHover = false;

      /* Hover state. hoverI is the card the pointer is on; lift[i] eases
         0 -> 1 so the shirt rises and settles instead of snapping. */
      var hoverI = -1;
      var focusI = -1;
      var overBox = false;
      var ptrIn = false;
      var ptrX = 0;
      var ptrY = 0;
      var lift = cards.map(function () { return 0; });
      var cosOf = cards.map(function () { return 0; });

      /* Drag state. `coasting` is what stops a flick from being killed the
         instant it is released: the pointer is usually still over the fan at
         that moment, and the hover rule would otherwise stop the wheel dead
         in the same frame the visitor let go. While coasting, the target is
         the ambient drift, so the throw decays naturally; once it has spent
         itself the hover rule takes over again and eases it to rest. */
      var dragging = false;
      var dragId = null;
      var dragX = 0;
      var dragT = 0;
      var dragMoved = 0;
      var dragVel = 0;
      var captured = false;
      var coasting = false;

      var raf = 0;
      var front = -1;

      /* Cached geometry, written by measure(), read every frame. */
      var stageW = 0;
      var rFactor = 0.38;
      var rMax = 500;
      var boxH = 0;

      var cardsBox = root.querySelector('[data-sp-cards]');

      /* Remember the server-rendered inline styles so cleanup can restore the
         exact fallback fan. */
      var initialStyles = cards.map(function (el) { return el.getAttribute('style') || ''; });
      var titleStyle = title ? (title.getAttribute('style') || '') : '';
      var ctaStyle = cta ? (cta.getAttribute('style') || '') : '';
      var chipRowStyle = chipRow ? (chipRow.getAttribute('style') || '') : '';

      root.classList.add('is-live');

      function measure() {
        stageW = stage.offsetWidth;
        var cs = window.getComputedStyle(stage);
        var f = parseFloat(cs.getPropertyValue('--sp-radius'));
        var m = parseFloat(cs.getPropertyValue('--sp-radius-max'));
        if (!isNaN(f) && f > 0) rFactor = f;
        if (!isNaN(m) && m > 0) rMax = m;

        /* The fan now owns its own band of the stage (a grid row), so the
           shirts can be sized against THAT rather than against the viewport.
           Publish the room available as a custom property and let the
           stylesheet clamp the card to it: a card can never be taller than the
           row it orbits in, at any viewport height, which is what used to send
           the front shirt up into the headline or down through the chips.

           0.68 of the row leaves the vertical swing (±RV) and the name/price
           line their share of the band, and spends everything else on the
           shirt, the art is the point of the section. Measured at 1440x850
           that is a 270px front shirt with ~20px still clear of the chips. */
        boxH = cardsBox ? cardsBox.clientHeight : stage.clientHeight;
        if (cardsBox && boxH > 0) {
          cardsBox.style.setProperty('--sp-cardmax', Math.round(boxH * 0.68) + 'px');
        }
      }

      function setFront(i) {
        front = i;
        for (var c = 0; c < chips.length; c++) {
          if (c === i) {
            chips[c].classList.add('is-front');
            chips[c].setAttribute('aria-current', 'true');
          } else {
            chips[c].classList.remove('is-front');
            chips[c].removeAttribute('aria-current');
          }
        }
        /* The same flag on the card itself. On phones the orbit is only ~250px
           wide, so neighbouring name captions print straight through each other
           (measured 55x19px of overprint at 320px); the stylesheet uses this to
           show just the front shirt's caption there. Desktop keeps them all,
           at >=750px the captions never touch. */
        for (var k = 0; k < cards.length; k++) {
          if (k === i) cards[k].classList.add('is-front');
          else cards[k].classList.remove('is-front');
        }
      }

      /* ---------------------------------------------------------------- *
       * The loop
       * ---------------------------------------------------------------- */
      function tick() {
        raf = requestAnimationFrame(tick);

        var vh = window.innerHeight;
        var r = root.getBoundingClientRect();      /* the one read per frame */
        if (r.bottom <= 0 || r.top >= vh) return;

        var now = performance.now();
        if (!spPlayed && r.top < vh * 0.55 && r.bottom > vh * 0.45) {
          spPlayed = true;
          spShowStart = now;
        }

        var t = spShowStart ? (now - spShowStart) / 1000 : 99;
        var showing = !!spShowStart && t < 4.2;

        /* Re-ask what the cursor is actually on, every frame. */
        if (canHover) hitTest();
        /* Keyboard focus outranks the pointer, a Tab is deliberate. A hand on
           the wheel outranks both: nothing should swell while it is being
           dragged, or the shirt grows and shrinks under the finger. */
        var liftI = dragging ? -1 : (focusI >= 0 ? focusI : hoverI);

        if (dragging) {
          /* The finger owns the wheel. onDragMove has already written spRot;
             the loop only renders. No drift, no easing, no snapping, nothing
             fights the hand. */
          spVel = 0;
        } else if (spSnapHold) {
          /* A BUBBLE was clicked. This is the one case where the wheel is
             driven to a destination rather than left to its own speed: ease
             onto the target and hand back to the drift when it lands. */
          spVel = 0;
          spRot += (spSnapT - spRot) * 0.14;
          if (Math.abs(spSnapT - spRot) < 0.05) {
            spRot = spSnapT;
            spSnapT = null;
            spSnapHold = false;
          }
        } else {
          /* Everything else is speed, never position. Hovering does not stop
             the wheel dead and it does not snap it to the nearest shirt, both
             of those yanked the thing you were looking at. It just takes the
             target speed to zero and lets it COAST to a rest wherever it
             happens to be; moving the pointer away hands the speed back the
             same way. Nothing jumps, in or out. */
          spSnapT = null;
          /* A throw outranks the hover rule until it has decayed back to the
             ambient drift, otherwise letting go under your own cursor stops
             the wheel in the same frame, which reads as the flick never having
             happened. */
          if (coasting && Math.abs(spVel) <= DRIFT * 1.25) coasting = false;
          var resting = !showing && !coasting && (spChipHover || overBox || liftI >= 0);
          var spTarget = showing ? (WHIP - WHIP_DECAY * cl((t - 1.4) / 1.2))
                       : (resting ? 0 : DRIFT);
          spVel += (spTarget - spVel) * (showing ? 0.07 : SETTLE);
          spRot += spVel;
        }

        /* Intro choreography */
        var intro = spShowStart ? cl(t / 0.9) : 0;
        if (title) {
          title.style.opacity = intro.toFixed(3);
          title.style.transform = 'translateY(' + ((1 - intro) * 46).toFixed(1) + 'px)';
        }

        var c2 = spShowStart ? cl((t - 3.1) / 0.7) : 0;
        var pe = c2 > 0.5 ? 'auto' : 'none';
        if (cta) {
          cta.style.opacity = c2.toFixed(3);
          cta.style.transform = 'translate(-50%,' + ((1 - c2) * 26).toFixed(1) + 'px)';
          cta.style.pointerEvents = pe;
        }
        if (chipRow) {
          chipRow.style.opacity = c2.toFixed(3);
          chipRow.style.pointerEvents = pe;
        }
        if (prog) prog.style.width = (showing ? (cl(t / 4.2) * 100).toFixed(1) : '0') + '%';

        /* Cards */
        /* The vertical radius is a share of the fan's OWN band, not of the
           viewport: the band is a grid row with a known height, so tying the
           swing to it keeps the orbit inside its lane on a 600px laptop and a
           1200px monitor alike. The old vh term is kept as the floor for the
           first frame, before the row has been measured. */
        var R2 = Math.min(stageW * rFactor, rMax);
        var RV = Math.min(R2 * 0.42, (boxH || vh * 0.5) * 0.16, 110);
        var bestI = 0;
        var bestC = -2;

        for (var i = 0; i < cards.length; i++) {
          var a = (spRot + i * step) * Math.PI / 180;
          var c = Math.cos(a);
          var s = Math.sin(a);
          var el = cards[i];
          cosOf[i] = c;

          /* The lift follows the pointer, and only the pointer. It does not
             care where the card is in its orbit, a shirt you are pointing at
             stays lifted while it drifts, and settles back when you leave.
             (The old zoom released itself once the card rotated past a cosine
             threshold, which is why it used to pop.) */
          var want = (i === liftI) ? 1 : 0;
          lift[i] += (want - lift[i]) * LIFT_EASE;
          if (lift[i] < 0.002) lift[i] = 0;
          var z = lift[i];

          var base = (0.42 + 0.58 * (c + 1) / 2) * (0.7 + 0.3 * intro);
          var op = (0.3 + 0.7 * (c + 1) / 2) * intro;

          /* Same orbit, every frame, hovered or not, the lift is added ON TOP
             of the card's real position rather than replacing it, so nothing
             ever travels across the stage to meet the cursor. */
          el.style.transform =
            'translate(-50%,-50%) translateX(' + (s * R2).toFixed(1) +
            'px) translateY(' + (c * RV - 26 - LIFT_RISE * z).toFixed(1) +
            'px) rotate(' + (s * 7 * (1 - z * 0.55)).toFixed(1) +
            'deg) scale(' + (base * (1 + LIFT_SCALE * z)).toFixed(3) + ')';
          /* A lifted shirt reads at full strength even from the back of the
             orbit, that is what makes it feel picked up. */
          el.style.opacity = (op + (1 - op) * z).toFixed(3);
          /* Lift bumps a card above its siblings (max 200) and never above the
             headline at 220. */
          el.style.zIndex = String(100 + Math.round(c * 100) + Math.round(z * 19));
          if (z > 0.5) el.classList.add('is-lifted');
          else el.classList.remove('is-lifted');

          if (c > bestC) { bestC = c; bestI = i; }
        }

        if (bestI !== front) setFront(bestI);
      }

      function start() { if (!raf) raf = requestAnimationFrame(tick); }
      function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }

      /* ---------------------------------------------------------------- *
       * Chips
       * ---------------------------------------------------------------- */
      function spGo(i) {
        var d = norm(-i * step - spRot);
        if (d > 180) d -= 360;
        spSnapT = spRot + d;
        spSnapHold = true;
      }

      function onChipEnter() { spChipHover = true; }
      function onChipLeave() { spChipHover = false; spSnapT = null; }

      function onChipClick(e) {
        var i = parseInt(e.currentTarget.getAttribute('data-index'), 10);
        if (!isNaN(i)) spGo(i);
      }

      var offs = [];
      if (chipRow) {
        offs.push(DTC.on(chipRow, 'pointerenter', onChipEnter));
        offs.push(DTC.on(chipRow, 'pointerleave', onChipLeave));
      }
      chips.forEach(function (chip) {
        offs.push(DTC.on(chip, 'click', onChipClick));
        /* Keyboard users get the same snap without a pointer. */
        offs.push(DTC.on(chip, 'focus', onChipClick));
      });

      /* ---------------------------------------------------------------- *
       * Hover, inspection, not navigation
       * ---------------------------------------------------------------- */
      /* The wheel rests on the BOX, not on the cards.
         Resting per-card looked equivalent and was not: a card that slides out
         from under a stationary cursor fires pointerleave, the wheel picks up
         speed, the next shirt drifts in and stops it again, a hover near the
         edge of a card became a wheel that stuttered forever. The fan box does
         not move, so testing the pointer against it is stable: inside means
         come to rest, and it stays at rest. */
      /* Which card is under the cursor is resolved by hit-testing once a frame
         (see hitTest(), called from tick), not by per-card pointerenter/leave.
         Enter/leave looked simpler and left shirts stuck lifted: the cards are
         moving under a stationary cursor, so a `leave` that never arrives, or
         arrives mid-transform, latches the lift on forever. A position test
         cannot get out of sync; every frame re-asks "what is actually under
         the cursor right now?".
         Touch has no hover at all: a tap there is a tap on the link. */
      function onStageMove(e) {
        ptrIn = true;
        ptrX = e.clientX;
        ptrY = e.clientY;
      }

      function onStageOut() {
        ptrIn = false;
        overBox = false;
        hoverI = -1;
      }

      function hitTest() {
        if (!ptrIn) { overBox = false; hoverI = -1; return; }

        if (cardsBox) {
          var b = cardsBox.getBoundingClientRect();
          overBox = ptrX >= b.left && ptrX <= b.right &&
                    ptrY >= b.top && ptrY <= b.bottom;
        }

        var el = document.elementFromPoint(ptrX, ptrY);
        var card = el && el.closest ? el.closest('[data-sp-card]') : null;
        hoverI = card ? cards.indexOf(card) : -1;
      }

      if (canHover) {
        offs.push(DTC.on(stage, 'pointermove', onStageMove, { passive: true }));
        offs.push(DTC.on(stage, 'pointerleave', onStageOut));
      }

      /* ---------------------------------------------------------------- *
       * Drag, grab the drop and spin it
       * ---------------------------------------------------------------- */
      /* Degrees per pixel at the front of the orbit, from the live radius, so
         the shirt under the finger keeps pace with it on any screen. */
      function degPerPx() {
        var R2 = Math.min(stageW * rFactor, rMax);
        return R2 > 0 ? DEG_PER_RAD / R2 : 0.1;
      }

      function onDragDown(e) {
        if (e.button != null && e.button !== 0) return;
        /* The chips and the CTA sit inside the stage; a press on either is a
           press on a control, not a grab. */
        if (e.target && e.target.closest &&
            e.target.closest('[data-sp-chips], [data-sp-cta]')) return;
        dragging = true;
        dragId = e.pointerId;
        dragX = e.clientX;
        dragT = e.timeStamp || Date.now();
        dragMoved = 0;
        dragVel = 0;
        spSnapT = null;
        spSnapHold = false;
        coasting = false;
        root.classList.add('is-dragging');
        /* NO pointer capture here. Capturing on pointerdown retargets the
           click to the capture element, so every tap on a shirt would be
           delivered to the fan box instead of the shirt's own <a>, the
           product links go dead and nothing says why. Capture is taken in
           onDragMove, once the travel proves this is a drag and not a tap. */
        start();
      }

      function onDragMove(e) {
        if (!dragging || e.pointerId !== dragId) return;
        var x = e.clientX;
        var now = e.timeStamp || Date.now();
        var dx = x - dragX;
        var dt = now - dragT;

        dragMoved += Math.abs(dx);
        /* Past the slop it is unambiguously a drag, so take the pointer: the
           gesture can now leave the fan box (or the window) and still finish.
           Taken here rather than on pointerdown so a tap keeps its click. */
        if (dragMoved > SLOP && !captured) {
          captured = true;
          try { cardsBox.setPointerCapture(dragId); } catch (_) {}
        }
        spRot += dx * degPerPx();

        /* Smoothed so one jittery sample cannot launch the wheel. Converted
           from deg/ms to the deg/frame the loop speaks. */
        if (dt > 0) {
          var perFrame = (dx * degPerPx()) / dt * 16.667;
          dragVel = dragVel * 0.7 + perFrame * 0.3;
        }
        dragX = x;
        dragT = now;
      }

      function endDrag(fling) {
        if (!dragging) return;
        dragging = false;
        root.classList.remove('is-dragging');
        if (dragId != null) {
          if (captured) { try { cardsBox.releasePointerCapture(dragId); } catch (_) {} }
          dragId = null;
        }
        captured = false;
        if (fling) {
          spVel = Math.max(-MAX_FLING, Math.min(MAX_FLING, dragVel));
          /* Only a real throw coasts. Letting go of a slow drag should simply
             hand back to the hover rule and settle. */
          coasting = Math.abs(spVel) > DRIFT * 1.5;
        } else {
          spVel = DRIFT;
          coasting = false;
        }
        dragVel = 0;
      }

      /* A drag that happens to end on top of a shirt must not open it. The
         capture phase beats the link. Keyboard activation arrives with
         detail 0 and no pointerType and is never suppressed. */
      function onDragClick(e) {
        if (!e.detail && (!('pointerType' in e) || !e.pointerType)) return;
        if (dragMoved > SLOP) {
          e.preventDefault();
          e.stopPropagation();
        }
        dragMoved = 0;
      }

      if (cardsBox) {
        offs.push(DTC.on(cardsBox, 'pointerdown', onDragDown));
        offs.push(DTC.on(cardsBox, 'pointermove', onDragMove));
        offs.push(DTC.on(cardsBox, 'pointerup', function (e) {
          if (dragging && e.pointerId !== dragId) return;
          endDrag(true);
        }));
        offs.push(DTC.on(cardsBox, 'pointercancel', function (e) {
          if (dragging && e.pointerId !== dragId) return;
          endDrag(false);
        }));
        offs.push(DTC.on(cardsBox, 'lostpointercapture', function () { endDrag(true); }));
        offs.push(DTC.on(cardsBox, 'click', onDragClick, true));
        offs.push(DTC.on(cardsBox, 'dragstart', function (e) { e.preventDefault(); }));
      }

      cards.forEach(function (card, i) {
        /* Keyboard parity, tabbing to a shirt brings it round and lifts it.
           A deliberate Tab is not a passive hover, so the pull is welcome. */
        offs.push(DTC.on(card, 'focus', function () { focusI = i; spGo(i); }));
        offs.push(DTC.on(card, 'blur', function () { if (focusI === i) focusI = -1; }));
      });

      offs.push(DTC.on(window, 'resize', measure, { passive: true }));
      offs.push(DTC.on(window, 'orientationchange', measure, { passive: true }));

      /* ---------------------------------------------------------------- *
       * Run the loop only while the section is on screen and the tab is
       * visible.
       * ---------------------------------------------------------------- */
      var onScreen = true;

      function sync() {
        if (onScreen && !document.hidden) start();
        else stop();
      }

      offs.push(DTC.on(document, 'visibilitychange', sync));

      var io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (entries) {
          onScreen = entries[entries.length - 1].isIntersecting;
          sync();
        }, { rootMargin: '200px 0px' });
        io.observe(root);
      }

      measure();
      setFront(Math.floor((cards.length - 1) / 2));
      sync();

      /* ---------------------------------------------------------------- *
       * Cleanup, the theme editor mounts and unmounts constantly.
       * ---------------------------------------------------------------- */
      return function cleanup() {
        stop();
        if (io) io.disconnect();
        /* Release the capture before the listeners go, or an editor unmount
           mid-drag leaves the pointer captured by a detached node. */
        if (dragId != null && captured && cardsBox) {
          try { cardsBox.releasePointerCapture(dragId); } catch (_) {}
        }
        dragging = false;
        dragId = null;
        captured = false;
        coasting = false;
        root.classList.remove('is-dragging');
        offs.forEach(function (off) { off(); });
        offs.length = 0;
        root.classList.remove('is-live');
        hoverI = -1;
        focusI = -1;
        overBox = false;
        ptrIn = false;
        cards.forEach(function (el, i) {
          lift[i] = 0;
          el.classList.remove('is-lifted');
          el.setAttribute('style', initialStyles[i]);
        });
        if (title) title.setAttribute('style', titleStyle);
        if (cta) cta.setAttribute('style', ctaStyle);
        if (chipRow) chipRow.setAttribute('style', chipRowStyle);
        if (prog) prog.style.width = '0';
      };
    }
  });
}());
